import { useTranslation } from 'react-i18next'

import { AppliedFilters, EmptyState, SortBy } from '@/components'
import { DEFAULT_LIMIT_PAGE } from '@/constants'
import { formatCurrency, formatLongDate, useParamsState } from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import { useSupplierTransactionList } from '@/services'
import { SupplyTransactionProps } from '@/types'
import {
  Button,
  EditIcon,
  FilterIcon,
  InputSearch,
  Pagination,
  Select,
  TableColumns,
  TableColumnsColumnProps,
  Tag,
  Text,
  Tooltip,
} from '@mercai/clever'
import { ReactNode, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  SupplierOrderDrawerForm,
  SupplyTransactionDrawerFilter,
} from './components'
import styles from './table-supplier-transaction.module.css'

import { getExpectedPaidClasses } from './table-supplier-transaction.constants'

export const TableSupplierTransaction = () => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementPage')

  const [filter, setFilter] = useParamsState({
    page: '1',
    limit: `${DEFAULT_LIMIT_PAGE}`,
    search: '',
    status: '',
    ordering: '',
  })

  const [isOpenDrawerFilter, setIsOpenDrawerFilter] = useState(false)

  const [params, setParams] = useParamsState({
    uuid: '',
  })

  const { data, isLoading } = useSupplierTransactionList(
    { ...filter, uuid: '', limit: filter.limit || `${DEFAULT_LIMIT_PAGE}` },
    true,
  )

  const [totalPages, setTotalPages] = useState(1)

  const handleOpenDrawerForm = (uuid: string) => {
    setParams({ uuid })
  }

  const getContentTotalFiltered = () => {
    const activeFilters = Object.keys(filter)?.filter(
      (key) =>
        !!filter[key] &&
        key !== 'page' &&
        key !== 'limit' &&
        key !== 'uuid' &&
        key !== 'ordering',
    ).length

    if (activeFilters > 0) {
      return (
        <Tag variant="primary" model="light" size="small">
          {activeFilters}
        </Tag>
      )
    }

    return ''
  }

  const handleFilter = (changedKey: string, newValue: string | number) => {
    if (changedKey === 'page') {
      setFilter({ ...filter, [changedKey]: `${newValue}` })
    } else {
      setFilter({ ...filter, [changedKey]: `${newValue}`, page: '1' })
    }
  }

  const onHandleSortBy = (ordering: string) => {
    if (ordering === filter.ordering) {
      return handleFilter('ordering', '')
    }

    handleFilter('ordering', ordering)
  }

  const statuses: Record<string, ReactNode> = {
    pending: (
      <Tag variant="warning" model="light">
        {tEnums('supplier_transaction_status_pending')}
      </Tag>
    ),
    paid: (
      <Tag variant="success" model="light">
        {tEnums('supplier_transaction_status_paid')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light">
        {tEnums('supplier_transaction_status_cancelled')}
      </Tag>
    ),
  }

  const SaleOrdersColumn = ({ record }: { record: SupplyTransactionProps }) => {
    const links = record?.saleOrders?.map((order, index) => (
      <>
        <Link
          key={order.uuid}
          to={`${ROUTES_ADMIN.ORDER_CENTER.ALL.SHOW.fullPath(order?.uuid)}`}
          target="_blank"
          className={styles.link}
        >
          {order?.reference || '-'}
        </Link>
        {index < (record.saleOrders?.length || 0) - 1 && ', '}
      </>
    ))
    return <div className={styles['sale-orders-column']}>{links}</div>
  }

  const columns: TableColumnsColumnProps<SupplyTransactionProps>[] = [
    {
      title: t('tableSupplierTransactionColumnSupplier'),
      key: 'shipments.supplier',
      render: ({ record }) => (
        <div className={styles['column-supplier']}>
          <Tooltip text={record?.supplier?.name || '-'} orientation="top">
            <Link
              target="_blank"
              to={ROUTES_ADMIN.SUPPLY.SUPPLIERS.SUPPLIERS.SHOW.fullPath(
                record?.supplier?.uuid || '',
              )}
              className={styles.link}
            >
              {record?.supplier?.name || '-'}
            </Link>
          </Tooltip>
        </div>
      ),
    },
    {
      title: t('tableSupplierTransactionColumnBuyerName'),
      key: 'buyer.companyName',
      width: '14rem',
      render: ({ record }) => {
        const buyerName = record?.buyer?.companyName || '-'
        return (
          <div>
            <Tooltip
              text={buyerName}
              orientation="top"
              className={styles.tooltip}
            >
              <div className={styles['company-name']}>{buyerName}</div>
            </Tooltip>
          </div>
        )
      },
    },
    {
      title: t('tableSupplierTransactionColumnSOs'),
      key: 'sos',
      width: '12rem',
      render: ({ record }) => <SaleOrdersColumn record={record} />,
    },
    {
      title: t('tableSupplierTransactionColumnFileId'),
      key: 'fileId',
      width: '10rem',
      render: ({ record }) => {
        const pos: string[] = []
        record.supplyOrders?.forEach((item) => {
          if (item?.supplyQuotation?.purchaseOrderCode)
            pos.push(item.supplyQuotation.purchaseOrderCode)
        })

        return pos.length > 0 ? pos.join(', ') : '-'
      },
    },
    {
      title: t('tableSupplierTransactionColumnExpectedPaid'),
      key: 'expectedPaymentDate',
      width: '10rem',
      render: ({ record }) => (
        <Text
          className={getExpectedPaidClasses(
            record?.expectedPaymentDate || '',
            record?.paymentAt,
          )}
        >
          {record?.expectedPaymentDate
            ? formatLongDate(record?.expectedPaymentDate)
            : '-'}
        </Text>
      ),
    },
    {
      title: t('tableSupplierTransactionColumnPaidAt'),
      key: 'paymentAt',
      sortKey: 'payment_at',
      width: '10rem',
      render: ({ record }) => (
        <Text>
          {record?.paymentAt ? formatLongDate(record?.paymentAt) : '-'}
        </Text>
      ),
    },
    {
      title: t('tableSupplierTransactionColumnTotal'),
      key: 'total',
      width: '11rem',
      render: ({ record }) => {
        return (
          <div>{formatCurrency(record?.value, 'es-MX', record?.currency)}</div>
        )
      },
    },
    {
      title: t('tableSupplierTransactionColumnCountry'),
      key: 'currency',
      dataKey: 'currency',
      width: '5.6rem',
    },
    {
      title: t('tableSupplierTransactionColumnBank'),
      key: 'destinationBank',
      dataKey: 'destinationBank',
      width: '7rem',
    },
    {
      title: t('tableSupplierTransactionColumnOrdersToPaid'),
      key: 'ordersToPaid',
      width: '7rem',
      render: ({ record }) => record?.supplyOrders?.length || '-',
    },
    {
      title: t('tableSupplierTransactionColumnPaidStatus'),
      key: 'status',
      width: '10rem',
      render: ({ record }) => statuses[record.status],
    },
    {
      title: t('tableSupplierTransactionColumnIsRefunded'),
      key: 'isRefunded',
      width: '8rem',
      render: ({ record }) =>
        record.isRefunded
          ? t('tableSupplierTransactionColumnIsRefundedYes')
          : t('tableSupplierTransactionColumnIsRefundedNo'),
    },
    {
      title: '',
      key: 'actions',
      width: '4.5rem',
      render: ({ record }) => (
        <div className={styles['column-actions']}>
          <Button
            variant="secondary"
            model="square"
            size="small"
            onClick={() => handleOpenDrawerForm(record?.uuid || '')}
          >
            <EditIcon />
          </Button>
        </div>
      ),
    },
  ]

  const sorts = [
    {
      title: t('orderingCreatedAtButton'),
      sortBy: 'created_at',
    },
  ]

  const ExpandedComponent = ({
    record,
  }: {
    record: SupplyTransactionProps
  }) => {
    return (
      <div className={styles['expanded-content']}>
        <div>
          <Text>
            <strong>
              {t('tableSupplierTransactionExpandedComponentSupplierOrders')}
            </strong>
          </Text>

          {record?.supplyOrders?.map((item) => (
            <Text key={item?.uuid}>
              <Link
                to={`${ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.PURCHASE_ORDER.fullPath()}?search=${item.reference}`}
                target="_blank"
                className={styles.link}
              >
                {item.reference || '-'}
              </Link>
            </Text>
          ))}
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (data?.data?.count) {
      setTotalPages(
        Math.ceil(data?.data?.count / +(filter?.limit || DEFAULT_LIMIT_PAGE)),
      )
    }
  }, [data])

  return (
    <div>
      <div className={styles.filter}>
        <div className={styles['filter-group']}>
          <InputSearch
            value={filter.search as string}
            onChange={(value) => handleFilter('search', value)}
            className={styles['search-input']}
            translations={{
              inputPlaceholder: t('tableSearchPlaceholder'),
            }}
          />

          <Button
            variant="secondary"
            onClick={() => setIsOpenDrawerFilter(true)}
          >
            <FilterIcon />
            {t('tableFilterButton')}
            {getContentTotalFiltered()}
          </Button>

          <SortBy
            sortBy={filter?.ordering}
            sorts={sorts}
            onHandleSortBy={onHandleSortBy}
          />
        </div>
      </div>

      <div className={styles['filtered-options']}>
        <AppliedFilters
          filtersApplied={filter}
          excludeKeys={['limit', 'page', 'ordering', 'uuid']}
          onRemoveFilter={(key) => handleFilter(key, '')}
          translations={{
            filterAppliedText: t('filterApplied'),
          }}
          keyTranslations={{
            currency: t('tableSupplyTransactionDrawerFilterCurrency'),
          }}
        />
      </div>

      <TableColumns
        isLoading={isLoading}
        columns={columns}
        data={data?.data?.results || []}
        onSortBy={onHandleSortBy}
        sortBy={filter?.ordering}
        expandableComponent={ExpandedComponent}
      />

      {!isLoading && (!data?.data || data?.data?.results?.length === 0) && (
        <EmptyState
          title={t('tableSupplierTransactionEmptyTitle')}
          description={t('tableSupplierTransactionEmptyDescription')}
        />
      )}

      {data?.data?.results && data.data.results.length >= 1 && (
        <div className={styles.pagination}>
          <div className={styles['pagination-show-registers']}>
            <Text>
              {t('tableShowRegisters', {
                showNumber:
                  (data?.data?.count || 0) < +filter.limit
                    ? data?.data?.count
                    : +filter.limit,
                totalNumber: data?.data?.count || 0,
              })}
            </Text>

            <Select
              value={filter.limit}
              onChange={(value) => handleFilter('limit', value)}
              options={[
                { label: '10', value: '10' },
                { label: '20', value: '20' },
                { label: '50', value: '50' },
                { label: '100', value: '100' },
              ]}
            />
          </div>

          <Pagination
            totalPages={totalPages}
            onChange={(page) => handleFilter('page', page)}
            currentPage={+filter.page as number}
          />
        </div>
      )}

      <SupplyTransactionDrawerFilter
        filter={filter}
        onHandleFilter={setFilter}
        isOpen={isOpenDrawerFilter}
        onHandleOpen={() => setIsOpenDrawerFilter(false)}
      />

      <SupplierOrderDrawerForm
        uuid={params.uuid}
        onHandleOpen={() => setParams({ uuid: '' })}
      />
    </div>
  )
}
